import LevelBar from '@app/src/components/LevelBar'
import ParentCellOrFragment from '@app/src/components/Table/Cells/ParentCellOrFragment'
import { getLevelDescription, getLevelLabel, getLevelTitle } from '@app/src/pages/Assessments/Assessment/utils'
import type Assessment from '@app/src/types/assessment'
import type { AssessmentTemplate } from '@app/src/types/resourceExplorer'
import { AssessmentLevelNamesType, AssessmentType } from '@app/src/types/resourceExplorer'
import { LabelFormat } from '@app/src/utils/assessmentLevelTranslations'
import { Box, ButtonBase, Stack, Tooltip, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

interface LevelProps {
  value: Assessment['totalLevel']
  levelsCount: number
  levelNamesType?: AssessmentLevelNamesType
  assessmentType?: AssessmentType
  thresholds: number[]
}

interface LevelCellProps {
  disableCell?: boolean
  disabled?: boolean
  noPublicData?: boolean
  onClick?: () => void
  companyName?: string
  assessmentTemplate?: AssessmentTemplate
  value: LevelProps['value']
}

export const Level: React.FC<LevelProps> = ({ value, levelsCount = 5, levelNamesType, assessmentType, thresholds }) => {
  return (
    <Stack alignItems="flex-start" whiteSpace="nowrap">
      <LevelBar
        value={value}
        levelsCount={levelsCount}
        levelNamesType={levelNamesType}
        assessmentType={assessmentType}
        thresholds={thresholds}
      />
    </Stack>
  )
}

const LevelCell: React.FC<LevelCellProps> = ({
  disableCell,
  disabled,
  value,
  noPublicData = false,
  onClick,
  companyName,
  assessmentTemplate,
}) => {
  const { formatMessage } = useIntl()

  const assessmentType = assessmentTemplate?.assessmentType || AssessmentType.BaselineAssessment
  const levelNamesType = assessmentTemplate?.levelNamesType ?? AssessmentLevelNamesType.Levels
  const levelsCount = assessmentTemplate?.levels ?? 5

  const tooltipTitle = value ? getLevelTitle(value, assessmentTemplate, formatMessage) : ''
  const tooltipDescription = value ? getLevelDescription(value, assessmentTemplate, companyName, formatMessage) : ''

  const levelLabel = value
    ? getLevelLabel(value, levelsCount, assessmentType, levelNamesType, formatMessage, LabelFormat.Basic)
    : ''

  const content = (
    <Box sx={({ palette }) => ({ opacity: disabled ? palette.action.disabledOpacity : 1 })} display="inline-flex">
      {onClick ? (
        <ButtonBase onClick={onClick}>
          <Level
            value={value}
            levelsCount={levelsCount}
            levelNamesType={levelNamesType}
            assessmentType={assessmentType}
            thresholds={assessmentTemplate?.levelThresholdPercentages ?? []}
          />
        </ButtonBase>
      ) : (
        <Level
          value={value}
          levelsCount={levelsCount}
          levelNamesType={levelNamesType}
          assessmentType={assessmentType}
          thresholds={assessmentTemplate?.levelThresholdPercentages ?? []}
        />
      )}
    </Box>
  )

  return (
    <ParentCellOrFragment disableCell={disableCell}>
      <Tooltip
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              maxWidth: 400,
            },
          },
        }}
        title={
          <Box p={1}>
            <Typography variant="overline" noWrap paragraph>
              {levelLabel}{' '}
              {tooltipTitle && formatMessage({ id: 'general.valueWithDotSeparatorBefore' }, { value: tooltipTitle })}
            </Typography>
            {noPublicData ? (
              <Typography variant="body2" lineHeight="2.8rem">
                {formatMessage({ id: 'schemas.request.noPublicData' }, { value })}
              </Typography>
            ) : (
              <Stack direction="row" alignItems="center" justifyContent="space-between" borderRadius={2}>
                <Typography variant="body2" lineHeight="2.5rem">
                  {tooltipDescription}
                </Typography>
              </Stack>
            )}
          </Box>
        }
      >
        {content}
      </Tooltip>
    </ParentCellOrFragment>
  )
}

export default LevelCell
